/* eslint-disable */

import { getAccumulationModeLabel } from "utils/accumulationModeLabels";
import { getMachineTypeName } from "utils/localMachineOrder";

const OPERATOR_FORMULA_TYPES = new Set(["+", "-", "*", "/"]);

const firstPresent = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const toFiniteOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const formatDisplayNumber = (value) => {
  const number = toFiniteOrNull(value);
  if (number == null) return null;
  return Number.isInteger(number) ? String(number) : String(number);
};

export const hasPresentationRent = (machine = {}) => {
  if (!machine) return false;

  const hasHistoricalRentSnapshot = Object.prototype.hasOwnProperty.call(machine, "HistoricalRent");
  if (hasHistoricalRentSnapshot) {
    return machine?.HistoricalRent?.Id != null;
  }

  const historicalRent = machine?.HistoricalRent;
  if (historicalRent) {
    if (historicalRent?.Id != null) return true;
  }

  const rent = machine?.Rent;
  if (rent) {
    if (rent?.IsActive === false) return false;
    if (rent?.Id != null) return true;
  }

  const rentId = machine?.RentId ?? machine?.LocalMachineRentId ?? null;
  const rentIsActive = machine?.RentIsActive ?? machine?.Rent?.IsActive ?? null;
  return rentId != null && rentIsActive !== false;
};

export const getPresentationFormula = (machine = {}) => {
  const hasHistoricalFormula = Object.prototype.hasOwnProperty.call(machine || {}, "HistoricalFormula");
  const historical = machine?.HistoricalFormula;
  if (hasHistoricalFormula && historical == null) return null;

  const current = machine?.Formula;
  const usesHistoricalSource = hasHistoricalFormula && historical != null;
  const source = usesHistoricalSource ? historical : current;

  const type = usesHistoricalSource
    ? firstPresent(source?.Type, source?.FormulaType)
    : firstPresent(
        source?.Type,
        source?.FormulaType,
        machine?.FormulaType,
        machine?.MachineFormulaType
      );
  const value = usesHistoricalSource
    ? firstPresent(source?.Value, source?.FormulaValue)
    : firstPresent(
        source?.Value,
        source?.FormulaValue,
        machine?.FormulaValue,
        machine?.MachineFormulaValue
      );
  const id = usesHistoricalSource
    ? firstPresent(source?.Id, source?.FormulaId)
    : firstPresent(
        source?.Id,
        source?.FormulaId,
        machine?.FormulaId,
        machine?.MachineFormulaId
      );

  if (type == null && value == null && id == null) return null;

  return {
    Id: id ?? null,
    Type: type == null ? null : String(type).trim(),
    Value: toFiniteOrNull(value),
  };
};

export const getCollectionMachinePresentation = (machine = {}) => {
  const empty = {
    isRented: hasPresentationRent(machine),
    type: getMachineTypeName(machine),
    formulaDisplay: null,
    tokenPrice: null,
    accumulationLabel: null,
    showFormula: false,
    showTokenPrice: false,
    showAccumulation: false,
  };

  if (empty.isRented) return empty;

  const type = empty.type;
  const formula = getPresentationFormula(machine);
  const formulaType = String(formula?.Type ?? "").trim();
  const formulaValue = formula?.Value;
  const hasFormulaValue = formulaValue !== null && formulaValue !== undefined;

  if (type === "Tragamoneda") {
    const formulaDisplay =
      OPERATOR_FORMULA_TYPES.has(formulaType) && hasFormulaValue
        ? `${formulaType} ${formatDisplayNumber(formulaValue)}`
        : "$";
    const accumulationLabel = getAccumulationModeLabel(machine?.IsAccumulative);

    return {
      ...empty,
      formulaDisplay,
      accumulationLabel,
      showFormula: true,
      showAccumulation: true,
    };
  }

  if (type === "Pool" || type === "Tejo" || type === "Peluchera") {
    if (!hasFormulaValue) return empty;
    return {
      ...empty,
      tokenPrice: formulaValue,
      showTokenPrice: true,
    };
  }

  return empty;
};

export const resolveMachineSerial = (...sources) => {
  for (const source of sources) {
    const serial = firstPresent(
      source?.SerialNumber,
      source?.Machine?.SerialNumber,
      source?.LocalMachine?.SerialNumber
    );
    if (serial != null && String(serial).trim() !== "") return String(serial).trim();
  }
  return null;
};

export const getDraftPersistedMoneyCollected = (machine = {}) => {
  const rawState = String(machine?.State ?? machine?.state ?? "").trim().toLowerCase();
  const visitMachine = machine?.VisitMachine ?? null;
  const isSaved =
    rawState === "saved" ||
    rawState === "guardada" ||
    (visitMachine && visitMachine.WasCollected !== false);

  if (!isSaved || visitMachine?.WasCollected === false) return null;

  const value = firstPresent(
    visitMachine?.MoneyCollected,
    visitMachine?.moneyCollected,
    machine?.PersistedMoneyCollected
  );
  return toFiniteOrNull(value);
};
